import PurchaseOrderItems from "./purchase-order-items";
import TotalPrice from "./total-price";
import Decimal from "decimal.js";

/**
 * 発注情報
 */
export default class PurchaseOrder {
  public constructor(
    public id: number = 0,
    public purchaseNumber: string = "",
    public projectId: number = 0,
    public projectName: string = "",
    public projectOwnerName: string = "",
    public title: string = "",
    public mention: string = "",
    public status: string = "created",
    public statusName: string = "",
    public estimateId: number = 0,
    public estimateNumber: string = "",
    public estimateTaxRate: number = 0,
    public estimateLoss: number = 0,
    public estimateDiscount: number = 0,
    public totalLossOfEstimateRow: number = 0,
    public vendorId: number = 0,
    public vendorName: string = "",
    public taxRate: number = 0,
    public items: PurchaseOrderItems = new PurchaseOrderItems(),
    public printsExcludeTax: boolean = false,
    public issuedAt: string = "",
    public dateOfIssue: string = "",
    public deliveryDate: string = ""
  ) {}

  public get profit(): number {
    return new Decimal(this.estimateTotal).minus(this.total).toNumber();
  }

  /**
   * 利益率 [%]
   */
  public get profitRate(): number {
    if (this.estimateTotal === 0) {
      return 0;
    }
    return new Decimal(this.profit)
      .div(this.estimateTotal)
      .mul(100)
      .toNumber();
  }

  public get estimateSubtotal(): number {
    return this.items.totalPrice.toNumber();
  }

  public get estimateSubtotalIncludeDiscount(): number {
    return new Decimal(this.estimateSubtotal).plus(this.discount).toNumber();
  }

  public get estimateTax(): number {
    return new TotalPrice(this.items.totalPrice)
      .tax(new Decimal(this.estimateTaxRate))
      .toNumber();
  }

  public get taxOfEstimateSubtotalIncludeDiscount(): number {
    return new TotalPrice(this.estimateSubtotalIncludeDiscount)
      .tax(new Decimal(this.estimateTaxRate))
      .toNumber();
  }

  public get estimateTotalExcludeDiscount(): number {
    return new TotalPrice(this.estimateSubtotal)
      .withTax(this.estimateTaxRate)
      .toNumber();
  }

  public get estimateTotal(): number {
    return new TotalPrice(this.estimateSubtotalIncludeDiscount)
      .withTax(this.estimateTaxRate)
      .toNumber();
  }

  public get subtotal(): TotalPrice {
    return new TotalPrice(this.items.totalLoss);
  }

  public get tax(): Decimal {
    return this.subtotal.tax(this.taxRate);
  }

  public get discount(): number {
    if (this.estimateLoss === 0) {
      return 0;
    }
    return Math.ceil(
      new Decimal(this.totalLossOfEstimateRow)
        .div(this.estimateLoss)
        .times(this.estimateDiscount)
        .toNumber()
    );
  }

  public get total(): Decimal {
    return this.subtotal.withTax(this.taxRate);
  }

  public toJSON(): any {
    return {
      id: this.id,
      purchaseNumber: this.purchaseNumber,
      projectId: this.projectId,
      projectName: this.projectName,
      staffName: this.projectOwnerName,
      title: this.title,
      mention: this.mention,
      status: this.status,
      statusName: this.statusName,
      estimateId: this.estimateId,
      estimateNumber: this.estimateNumber,
      estimateTotal: this.estimateTotal,
      estimateTotalExcludeDiscount: this.estimateTotalExcludeDiscount,
      estimateSubtotal: this.estimateSubtotal,
      estimateSubtotalIncludeDiscount: this.estimateSubtotalIncludeDiscount,
      estimateTax: this.estimateTax,
      taxOfEstimateSubtotalIncludeDiscount: this
        .taxOfEstimateSubtotalIncludeDiscount,
      estimateTaxRate: this.estimateTaxRate,
      estimateLoss: this.estimateLoss,
      estimateDiscount: this.estimateDiscount,
      totalLossOfEstimateRow: this.totalLossOfEstimateRow,
      vendorId: this.vendorId,
      vendorName: this.vendorName,
      loss: this.subtotal.toNumber(),
      lossTotal: this.total.toNumber(),
      tax: this.tax.toNumber(),
      taxRate: this.taxRate,
      discount: this.discount,
      items: this.items.toJSON(),
      printsExcludeTax: this.printsExcludeTax,
      issuedAt: this.issuedAt,
      profit: this.profit,
      profitRate: this.profitRate.toFixed(2),
      dateOfIssue: this.dateOfIssue,
      deliveryDate: this.deliveryDate
    };
  }
}
